/**
 * VoidMothership.ts
 * The World 2 boss — a capital ship that drifts in from off-screen-right,
 * holds station near the right edge of the viewport, and tanks ship
 * projectiles until it dies.
 *
 * Entity layer only: owns the mutable state + the spawn/damage lifecycle.
 * The top-level phase machine (ENTRY → HOLD → DEFEATED + shield cycle)
 * lives in `../engine/voidMothershipFsm.ts`; the ram / drone / missile /
 * turret subsystems each live in their own `bossXxx.ts` siblings under
 * `../engine/`. Same "data + delegation" split the ram already uses.
 *
 * NO React imports, NO Skia imports, NO async.
 *
 * Phase 4 — World 2: Starfield Drift.
 *
 * @module VoidMothership
 */

import { BaseEntity } from './BaseEntity';
import { applyBossDamage } from '../engine/bossDamage';
import { stepVoidMothership } from '../engine/voidMothershipFsm';
import type { LevelRuntime } from '../engine/gameState';
import {
  VOID_MOTHERSHIP_WIDTH,
  VOID_MOTHERSHIP_HEIGHT,
  VOID_MOTHERSHIP_HP,
  VOID_MOTHERSHIP_ENTRY_SPEED,
  VOID_MOTHERSHIP_SPAWN_X_OFFSET,
  VOID_MOTHERSHIP_HOLD_Y,
  VOID_MOTHERSHIP_TURRET_RAMP_IN_S,
  VOID_MOTHERSHIP_DRONE_SPAWN_RAMP_IN_S,
  VOID_MOTHERSHIP_RAM_RAMP_IN_S,
  VOID_MOTHERSHIP_MISSILE_RAMP_IN_S,
} from '../../constants/bossConstants';

/**
 * Lifecycle state machine for the boss greybox slice.
 *
 * @remarks Kept as a string-literal union (not a TS enum) to match the
 * existing entity convention in this codebase.
 */
export type VoidMothershipState = 'ENTRY' | 'HOLD' | 'DEFEATED';

/**
 * Phase-3 ram-charge sub-state, driven by `bossRam.stepBossRam()` while
 * top-level {@link VoidMothershipState} is `HOLD`.
 *
 *  - `IDLE`      — camera-locked, `ramCooldown` ticks down.
 *  - `TELEGRAPH` — camera-locked, `ramPhaseTimer` ticks down (visual windup).
 *  - `CHARGE`    — driving LEFT at `..._RAM_SPEED` for `..._RAM_DISTANCE` px.
 *  - `RETURN`    — driving RIGHT at `..._RAM_SPEED` back to the live HOLD anchor.
 */
export type RamPhase = 'IDLE' | 'TELEGRAPH' | 'CHARGE' | 'RETURN';

/**
 * The Void Mothership. Singleton — only one is ever alive in
 * `spaceState.boss`, so this class is NOT pooled (unlike the per-kind
 * enemy pools in `spaceEnemyPools`).
 */
export class VoidMothership extends BaseEntity {
  /** Discriminator the renderer / collision layer can read without
   *  `instanceof` checks. Distinguishes the boss from regular enemies. */
  public readonly kind = 'boss' as const;

  /** Remaining HP. Zero or below marks the boss dead. */
  public hp = 0;

  /** True while the boss is in the active scene (between spawn and death). */
  public active = false;

  /** Current state-machine slot — see {@link VoidMothershipState}. */
  public state: VoidMothershipState = 'DEFEATED';

  /**
   * Seconds remaining until the next turret shot. Decrements during HOLD
   * only. `bossSystem` reads this each tick after the boss update; when
   * `<= 0` it fires a `BossProjectile` and resets the cooldown to
   * `VOID_MOTHERSHIP_TURRET_COOLDOWN_S`. Initialised to
   * `VOID_MOTHERSHIP_TURRET_RAMP_IN_S` on ENTRY→HOLD so the player gets
   * a one-second beat before the first shot.
   */
  public turretCooldown = 0;

  /**
   * Seconds remaining until the next boss-spawned fighter drone.
   * Decrements during HOLD only — mirrors `turretCooldown`. `bossSystem`
   * reads this each tick; when `<= 0` it acquires a `FighterDrone` from
   * the shared enemy pool, places it near the boss, and resets the
   * cooldown to `VOID_MOTHERSHIP_DRONE_SPAWN_COOLDOWN_S`. Initialised to
   * `VOID_MOTHERSHIP_DRONE_SPAWN_RAMP_IN_S` on ENTRY→HOLD so the first
   * wave lands a beat AFTER the first turret bolt.
   */
  public droneSpawnCooldown = 0;

  /**
   * True while the boss is in its `SHIELDED` sub-phase of HOLD.
   * Damage is suppressed in this window — `takeDamage()` consumes the
   * projectile via the caller's `dead = true` flag but the boss's HP is
   * not decremented. The SHIELDED ↔ EXPOSED cadence is driven by
   * {@link ../engine/voidMothershipFsm | stepVoidMothership}.
   *
   * Always `false` outside HOLD so an ENTRY-phase hit (boss still
   * off-screen — currently gated by the collision resolver) would not
   * be silently absorbed.
   */
  public isShielded = false;

  /**
   * Seconds remaining in the current `SHIELDED`/`EXPOSED` sub-phase.
   * Decrements during HOLD only — when it reaches zero the boss flips
   * `isShielded` and reloads `shieldTimer` from the duration constant
   * for the new sub-phase. Initialised to
   * `VOID_MOTHERSHIP_SHIELD_RAMP_IN_S` on ENTRY→HOLD so the very first
   * `EXPOSED` window arrives on the regular cadence.
   */
  public shieldTimer = 0;

  /**
   * Accumulated rotation angle (radians) of the shield-ring visual.
   * Ticked by `update(dt)` at
   * `VOID_MOTHERSHIP_SHIELD_ROTATION_RAD_S` regardless of
   * `isShielded` so the rotation is continuous across SHIELDED ↔
   * EXPOSED flips — the renderer reads this value only while
   * `isShielded && state === 'HOLD'`. Wrapped into `[0, 2π)` each
   * tick to prevent unbounded float growth over a long boss fight.
   * Also consumed by gameplay: {@link takeDamage} passes this value
   * to `isShieldedHitBlocked` so the directional shield gate matches
   * the rendered node ring frame-for-frame (see
   * {@link ../engine/shieldGate}).
   */
  public shieldAngle = 0;

  /**
   * Seconds remaining in the current hit-flash window. Set by
   * {@link takeDamage} on every hit that actually decrements HP (i.e.
   * not while {@link isShielded}); ticked down in {@link update}
   * regardless of state so a flash mid-CHARGE finishes cleanly. The
   * renderer paints the hull bright while this is `> 0` and falls back
   * to the default fill otherwise. Renderer-only — no gameplay reads.
   */
  public hitFlashTimer = 0;

  /**
   * Ram-charge sub-state — see {@link RamPhase}. Driven by
   * `bossRam.stepBossRam()`, not by this entity, so the kinematic logic
   * lives next to the other boss-system siblings instead of bloating
   * this file past the 300-line budget. The entity exposes the field
   * and skips its own camera-lock during `CHARGE` / `RETURN` so the
   * stepper can drive `x`.
   */
  public ramPhase: RamPhase = 'IDLE';

  /** Seconds until the next ram windup. Ticked by `stepBossRam` only
   *  while in HOLD + `ramPhase === 'IDLE'`. */
  public ramCooldown = 0;

  /** Seconds remaining in the current `TELEGRAPH` window. */
  public ramPhaseTimer = 0;

  /** World-space px still to travel in the CHARGE leg. */
  public ramDistanceRemaining = 0;

  /** Seconds until the next ram-trail explosion spawn. Ticked by
   *  `stepBossRam` only during `ramPhase === 'CHARGE'`; reset to
   *  `VOID_MOTHERSHIP_RAM_TRAIL_INTERVAL_S` on each spawn. Reseeded
   *  to 0 on TELEGRAPH→CHARGE so the ignition tick drops a burst on
   *  the very first CHARGE frame instead of waiting one interval. */
  public ramTrailTimer = 0;

  /**
   * Seconds remaining until the next homing-missile launch. Decrements
   * during HOLD only — mirrors `turretCooldown` / `droneSpawnCooldown`.
   * `bossSystem` reads this each tick; when `<= 0` it calls
   * `fireBossMissile()` and resets the cooldown to
   * `VOID_MOTHERSHIP_MISSILE_COOLDOWN_S` (pool-miss-no-burn). Initialised
   * to `VOID_MOTHERSHIP_MISSILE_RAMP_IN_S` on ENTRY→HOLD so the first
   * missile arrives after the player has read the other tells.
   */
  public missileCooldown = 0;

  /**
   * Accumulated bob-phase seconds for the HOLD-state vertical bob.
   * Ticked by {@link stepVoidMothership} each frame while the boss is
   * in HOLD. Consumed as the argument to `Math.sin` against
   * {@link VOID_MOTHERSHIP_BOB_FREQ_HZ} so the boss's Y is
   * `HOLD_Y + sin(2π · freq · bobTimer) · amplitude`. Wrapped into the
   * `[0, 1/freq)` interval each tick to prevent unbounded float
   * growth over a long fight (the sine result is identical, the
   * stored value just stays bounded).
   */
  public bobTimer = 0;

  /**
   * Accumulated sweep-phase seconds for the HOLD-state lateral X sweep.
   * Kept independent from {@link bobTimer} so each sine wave wraps at
   * its own period — sharing one timer wrapped at the slower frequency
   * snapped the faster wave's phase every wrap (PR #124 review).
   * Wrapped into `[0, 1/sweepFreq)` each tick to bound float growth.
   */
  public sweepTimer = 0;

  constructor() {
    super(0, 0, VOID_MOTHERSHIP_WIDTH, VOID_MOTHERSHIP_HEIGHT);
    this.dead = true;
  }

  /**
   * Spawn the boss just past the right edge of the viewport so the first
   * ticks of ENTRY are off-screen and the boss slides into view.
   *
   * @param cameraX - Current camera left-edge X (world space). Passed in
   *                  rather than read so this method can be tested
   *                  without touching the `spaceState` singleton.
   */
  public spawn(cameraX: number): void {
    this.x = cameraX + VOID_MOTHERSHIP_SPAWN_X_OFFSET;
    this.y = VOID_MOTHERSHIP_HOLD_Y;
    this.vx = -VOID_MOTHERSHIP_ENTRY_SPEED;
    this.vy = 0;
    this.hp = VOID_MOTHERSHIP_HP;
    this.state = 'ENTRY';
    this.dead = false;
    this.active = true;
    // Cooldown is only ticked in HOLD; the initial value here is
    // overwritten on ENTRY→HOLD transition. Set defensively so a stray
    // pre-HOLD reader cannot see a stale value from a previous life.
    this.turretCooldown = VOID_MOTHERSHIP_TURRET_RAMP_IN_S;
    this.droneSpawnCooldown = VOID_MOTHERSHIP_DRONE_SPAWN_RAMP_IN_S;
    this.missileCooldown = VOID_MOTHERSHIP_MISSILE_RAMP_IN_S;
    // Shield is inert outside HOLD. Defensive reset so a previous life's
    // EXPOSED window can't carry over into the new ENTRY slide.
    this.isShielded = false;
    this.shieldTimer = 0;
    this.shieldAngle = 0;
    this.hitFlashTimer = 0;
    // Ram inert outside HOLD. Defensive reset; cooldown is reseeded on
    // ENTRY→HOLD below.
    this.ramPhase = 'IDLE';
    this.ramCooldown = VOID_MOTHERSHIP_RAM_RAMP_IN_S;
    this.ramPhaseTimer = 0;
    this.ramDistanceRemaining = 0;
    this.ramTrailTimer = 0;
    // Bob inert outside HOLD; defensive reset so a previous life's
    // accumulated phase can't carry over into ENTRY (Y is held at
    // HOLD_Y during ENTRY by the FSM, not by this field, but a stale
    // value would jolt the bob the moment HOLD takes over).
    this.bobTimer = 0;
    // Same defensive reset for the lateral sweep — a stale phase from
    // a previous life would jolt X the moment HOLD takes over.
    this.sweepTimer = 0;
  }

  /**
   * Apply `amount` damage. Marks the boss dead when HP reaches 0.
   *
   * Thin delegation to {@link ../engine/bossDamage | applyBossDamage} so
   * the shield-gate + hit-flash logic lives next to the other engine
   * helpers and the entity stays under the 300-line budget.
   *
   * @param amount - Damage in HP units. Non-positive values are ignored.
   * @param hitX   - Optional world-space X of the projectile impact.
   *                 When omitted, shielded hits are always blocked.
   * @param hitY   - Optional world-space Y. Must accompany `hitX`.
   * @returns `true` if the boss was killed by this hit.
   */
  public takeDamage(amount: number, hitX?: number, hitY?: number): boolean {
    return applyBossDamage(this, amount, hitX, hitY);
  }

  /**
   * Advance one fixed timestep.
   *
   * Thin delegation to {@link stepVoidMothership} — the entity owns
   * the data + spawn/damage lifecycle; the engine-side stepper owns
   * the phase state machine, weapon cooldowns, and shield cycle.
   * Mirrors the `bossRam` / `bossDroneSpawner` / `bossMissileBarrage`
   * split (Phase 5 Tech Debt — see ROADMAP.md).
   *
   * @param dt    - Fixed timestep in seconds.
   * @param level - Unused (space has no level geometry).
   */
  public update(dt: number, level: LevelRuntime): void {
    void level;
    stepVoidMothership(this, dt);
  }
}
